import { useSyncExternalStore } from 'react';

/**
 * Where the customer wants food delivered. Kept on this device (it is not a secret) so the
 * home feed loads at once next time. Comes from GPS, a saved address or a town centre.
 */
export interface DeliveryPoint {
  lat: number;
  lng: number;
  /** Short label for the top bar ("Home", "Current location", "Ayodhya (town centre)"). */
  label: string;
  /** Second line (street / area), when known. */
  line: string | null;
  source: 'gps' | 'address' | 'town';
  addressId: string | null;
}

const KEY = 'capsico:customer:point';

function read(): DeliveryPoint | null {
  try {
    const raw = window.localStorage.getItem(KEY);
    if (!raw) return null;
    const value = JSON.parse(raw) as Partial<DeliveryPoint>;
    if (typeof value.lat !== 'number' || typeof value.lng !== 'number') return null;
    return {
      lat: value.lat,
      lng: value.lng,
      label: typeof value.label === 'string' ? value.label : '',
      line: typeof value.line === 'string' ? value.line : null,
      source: value.source === 'address' || value.source === 'town' ? value.source : 'gps',
      addressId: typeof value.addressId === 'string' ? value.addressId : null,
    };
  } catch {
    return null;
  }
}

let current: DeliveryPoint | null = typeof window === 'undefined' ? null : read();
const listeners = new Set<() => void>();

export function setDeliveryPoint(point: DeliveryPoint | null): void {
  current = point;
  try {
    if (point) window.localStorage.setItem(KEY, JSON.stringify(point));
    else window.localStorage.removeItem(KEY);
  } catch {
    // ignore
  }
  for (const listener of listeners) listener();
}

export function useDeliveryPoint(): DeliveryPoint | null {
  return useSyncExternalStore(
    (listener) => {
      listeners.add(listener);
      return () => listeners.delete(listener);
    },
    () => current,
    () => null,
  );
}

/** Rounded so nearby taps share one cache entry (~110 m). */
export function pointKey(point: { lat: number; lng: number } | null): string {
  return point ? `${point.lat.toFixed(3)},${point.lng.toFixed(3)}` : 'none';
}
